import React, { useCallback, useState } from 'react';
import { RowFileTypeEnums } from '../Enums/RowFileTypeEnums';
import styled from '@emotion/styled';
import Skeleton from 'react-loading-skeleton';
import { WordDocumentPreview } from './WordDocumentPreview';
import { PreviewFrame } from './PreviewFrame';

export const PREVIEWABLE_TYPES = new Set<string>([
  RowFileTypeEnums.Pdf,
  RowFileTypeEnums.Png,
  RowFileTypeEnums.Jpg,
  RowFileTypeEnums.Jpeg,
  RowFileTypeEnums.Gif,
  // Served as the PDF rendition Salesforce makes of them, not as the raw file.
  RowFileTypeEnums.Word,
  RowFileTypeEnums.WordX,
  RowFileTypeEnums.WordT,
]);

export const isPreviewableType = (fileType: string): boolean =>
  PREVIEWABLE_TYPES.has(fileType);

type Props = {
  type: string;
  url: string;
  width?: string | number;
};

export const DocumentPreview: React.FC<React.PropsWithChildren<Props>> = ({
  type,
  url,
  width = '70%',
}) => {
  const [isPreviewLoading, setIsPreviewLoading] = useState(true);
  const stopLoading = useCallback(() => setIsPreviewLoading(false), []);

  const renderPreviewLoader = () => {
    if (!isPreviewLoading) {
      return null;
    }
    return (
      <PreviewLoader className="w-100 position-absolute mt-n1">
        <SkeletonWrapper
          width={width}
          height={'100%'}
          className="rounded-0 position-relative"
        />
      </PreviewLoader>
    );
  };

  const renderPreview = () => {
    switch (type) {
      case RowFileTypeEnums.Pdf:
        return <PreviewFrame url={url} width={width} onLoad={stopLoading} />;

      case RowFileTypeEnums.Word:
      case RowFileTypeEnums.WordX:
      case RowFileTypeEnums.WordT:
        return (
          <WordDocumentPreview url={url} width={width} onLoad={stopLoading} />
        );

      case RowFileTypeEnums.Png:
      case RowFileTypeEnums.Jpg:
      case RowFileTypeEnums.Jpeg:
      case RowFileTypeEnums.Gif:
        return (
          <div className="d-flex align-items-center justify-content-center w-100 h-100">
            <Img src={url} onLoad={stopLoading} alt="preview" />
          </div>
        );
      default:
        return null;
    }
  };

  const preview = renderPreview();

  // Only a rendered preview can clear the loader, via its onLoad. Showing the
  // loader without one leaves it up forever, covering whatever is behind it.
  return (
    <>
      {preview && renderPreviewLoader()}
      {preview}
    </>
  );
};

const Img = styled.img`
  object-fit: contain;
  max-width: 100%;
  max-height: 100%;
`;

const SkeletonWrapper = styled(Skeleton)`
  top: -0.063rem;
`;

const PreviewLoader = styled.div`
  width: 70%;
  z-index: 1;
  height: 100%;
`;
